import React, { useState } from "react";
import { useLanguage, languages } from "../context/LanguageContext";

// Material UI components
import {
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  IconButton,
  Fade,
  Box,
  Typography,
  useTheme,
} from "@mui/material";

// Material UI icons
import TranslateIcon from "@mui/icons-material/Translate";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CheckIcon from "@mui/icons-material/Check";

// Flag icons
const getFlagEmoji = (countryCode) => {
  switch (countryCode) {
    case "en":
      //return US flag emoji
      return "🇺🇸";
    case "de":
      return "🇩🇪";
    case "el":
      return "🇬🇷";
    case "bg":
      return "🇧🇬";
    default:
      return "🌐";
  }
};

const LanguageSelector = ({
  variant = "icon",
  size = "medium",
  color = "inherit",
}) => {
  const theme = useTheme();
  const { currentLanguage, changeLanguage, t } = useLanguage();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = (langCode) => {
    changeLanguage(langCode);
    handleCloseMenu();
  };

  // Custom pill style for the button
  const pillButtonSx = {
    borderRadius: 999,
    px: 2.5,
    py: 0.5,
    fontWeight: 600,
    color: '#fff',
    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
    boxShadow: theme.shadows[2],
    textTransform: 'none',
    transition: 'all 0.2s',
    '&:hover': {
      background: `linear-gradient(90deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
      boxShadow: theme.shadows[4],
      transform: 'translateY(-2px) scale(1.04)',
    },
    minHeight: 40,
    minWidth: 120,
    fontSize: '1rem',
    letterSpacing: 0.2,
    gap: 1.5,
  };

  // Helper to render the correct language display based on variant
  const renderLanguageDisplay = () => {
    if (variant === "icon") {
      // Collapsed: just an icon button with the flag
      return (
        <Tooltip title={t("changeLanguage") || "Change language"}>
          <IconButton
            onClick={handleOpenMenu}
            size={size}
            sx={{
              borderRadius: '50%',
              background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              color: '#fff',
              width: 40,
              height: 40,
              boxShadow: theme.shadows[2],
              '&:hover': {
                background: `linear-gradient(90deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
                boxShadow: theme.shadows[4],
              },
              fontSize: '1.5rem',
            }}
            aria-haspopup="true"
            aria-expanded={open ? "true" : "false"}
            aria-controls={open ? "language-menu" : undefined}
          >
            {getFlagEmoji(currentLanguage)}
          </IconButton>
        </Tooltip>
      );
    }
    // Expanded: pill style
    return (
      <Button
        onClick={handleOpenMenu}
        startIcon={<TranslateIcon sx={{ color: '#fff' }} />}
        endIcon={<KeyboardArrowDownIcon sx={{ color: '#fff' }} />}
        aria-haspopup="true"
        aria-expanded={open ? "true" : "false"}
        aria-controls={open ? "language-menu" : undefined}
        sx={pillButtonSx}
        disableElevation
      >
        {getFlagEmoji(currentLanguage)}
        <Box component="span" sx={{ ml: 1, fontWeight: 600, color: '#fff' }}>
          {languages[currentLanguage].name}
        </Box>
      </Button>
    );
  };

  return (
    <>
      {renderLanguageDisplay()}
      <Menu
        id="language-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleCloseMenu}
        slotProps={{ transition: { component: Fade } }}
        transitionDuration={200}
        PaperProps={{
          elevation: 6,
          sx: {
            minWidth: 200,
            borderRadius: 3,
            mt: 1,
            p: 0.5,
            boxShadow: theme.shadows[8],
            background: theme.palette.background.paper,
            '& .MuiMenuItem-root': {
              borderRadius: 2,
              mx: 0.5,
              my: 0.5,
              fontWeight: 500,
              fontSize: '1rem',
              transition: 'background 0.15s',
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        {Object.values(languages).map((lang) => (
          <MenuItem
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            selected={currentLanguage === lang.code}
            sx={{
              py: 1.25,
              display: 'flex',
              alignItems: 'center',
              background: currentLanguage === lang.code
                ? `linear-gradient(90deg, ${theme.palette.primary.light} 0%, ${theme.palette.secondary.light} 100%)`
                : 'none',
              color: currentLanguage === lang.code ? theme.palette.primary.contrastText : 'inherit',
              fontWeight: currentLanguage === lang.code ? 700 : 500,
              '&:hover': {
                background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                color: '#fff',
              },
            }}
          >
            <Box sx={{ fontSize: '1.3rem', mr: 2 }}>{getFlagEmoji(lang.code)}</Box>
            <Typography variant="body1" sx={{ flex: 1 }}>{lang.name}</Typography>
            {currentLanguage === lang.code && (
              <ListItemIcon sx={{ minWidth: 'auto', ml: 1, color: '#fff' }}>
                <CheckIcon fontSize="small" />
              </ListItemIcon>
            )}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default LanguageSelector;
